import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {App, Button, Card, Form, Radio, Steps, Typography} from 'antd';
import {AssessmentRequest, assessmentService} from '../../services/assessmentService';
import {useAuth} from '../../contexts/AuthContext';

const {Title, Text} = Typography;

interface FormValues {
    question1Reason: number;
    question2RiskLevel: number;
    question3Amount: number;
}

const AssessmentPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const {user} = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm<FormValues>();
    const [submitting, setSubmitting] = useState(false);

    // 动机选项
    const reasonOptions = [
        {label: '想要获得被动收入', value: 1},
        {label: '对技术感兴趣，想学习量化交易', value: 2},
        {label: '优化现有投资策略', value: 3},
        {label: '其他', value: 4},
    ];

    // 步骤配置
    const steps = [
        {
            title: '了解动机',
            description: '为什么想接触量化交易',
        },
        {
            title: '风险评估',
            description: '您的风险承受能力',
        },
        {
            title: '资金规划',
            description: '准备投入的资金',
        },
    ];

    // 风险等级选项
    const riskLevelOptions = [
        {label: 'R1 - 低风险（追求年化5-10%，最大亏损5%）', value: 1},
        {label: 'R2 - 中低风险（追求年化10-20%，最大亏损10%）', value: 2},
        {label: 'R3 - 中风险（追求年化20-40%，最大亏损20%）', value: 3},
        {label: 'R4 - 中高风险（追求年化40-80%，最大亏损30%）', value: 4},
        {label: 'R5 - 高风险（追求年化80%+，最大亏损50%+）', value: 5},
    ];

    // 资金投入选项
    const amountOptions = [
        {label: '小于 1 万元', value: 1},
        {label: '1 - 5 万元', value: 2},
        {label: '5 - 10 万元', value: 3},
        {label: '10 万元以上', value: 4},
    ];

    // 下一步
    const handleNextStep = async () => {
        try {
            if (0 === currentStep) {
                await form.validateFields(['question1Reason']);
            } else if (1 === currentStep) {
                await form.validateFields(['question2RiskLevel']);
            }
            setCurrentStep(currentStep + 1);
        } catch {
            // 验证失败，不跳转
        }
    };

    // 上一步
    const handlePrevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    // 提交测评
    const handleSubmit = async (values: FormValues) => {
        if (!user?.id) {
            messageApi.error('用户未登录');
            return;
        }

        setSubmitting(true);
        try {
            const request: AssessmentRequest = {
                question1Reason: values.question1Reason,
                question2RiskLevel: values.question2RiskLevel,
                question3Amount: values.question3Amount,
            };

            await assessmentService.submitAssessment(user.id, request);
            messageApi.success('测评完成，欢迎使用！');
            navigate('/');
        } catch (error: any) {
            messageApi.error(error.message || '提交失败，请稍后重试');
        } finally {
            setSubmitting(false);
        }
    };

    // 渲染问题内容（所有问题都渲染，只是通过 hidden 控制显示）
    const renderQuestionContent = () => (
        <>
            {/* 问题1：动机 */}
            <div style={{display: currentStep === 0 ? 'block' : 'none'}}>
                <Text style={{color: 'var(--pt-text-secondary)', display: 'block', marginBottom: 16}}>
                    请选择您想接触量化交易的主要原因
                </Text>
                <Form.Item
                    name="question1Reason"
                    rules={[{required: true, message: '请选择您的动机'}]}
                >
                    <Radio.Group>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                            {reasonOptions.map(option => (
                                <Radio
                                    key={option.value}
                                    value={option.value}
                                    style={{color: 'var(--pt-text-primary)'}}
                                >
                                    {option.label}
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
            </div>

            {/* 问题2：风险承受能力 */}
            <div style={{display: currentStep === 1 ? 'block' : 'none'}}>
                <Text style={{color: 'var(--pt-text-secondary)', display: 'block', marginBottom: 16}}>
                    请选择您的风险承受能力等级
                </Text>
                <Form.Item
                    name="question2RiskLevel"
                    rules={[{required: true, message: '请选择风险承受能力'}]}
                >
                    <Radio.Group>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                            {riskLevelOptions.map(option => (
                                <Radio
                                    key={option.value}
                                    value={option.value}
                                    style={{color: 'var(--pt-text-primary)'}}
                                >
                                    {option.label}
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
            </div>

            {/* 问题3：投入资金 */}
            <div style={{display: currentStep === 2 ? 'block' : 'none'}}>
                <Text style={{color: 'var(--pt-text-secondary)', display: 'block', marginBottom: 16}}>
                    请选择您准备投入的资金量
                </Text>
                <Form.Item
                    name="question3Amount"
                    rules={[{required: true, message: '请选择投入资金'}]}
                >
                    <Radio.Group>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                            {amountOptions.map(option => (
                                <Radio
                                    key={option.value}
                                    value={option.value}
                                    style={{color: 'var(--pt-text-primary)'}}
                                >
                                    {option.label}
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
            </div>
        </>
    );

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'var(--pt-bg-base)',
        }}>
            <Card
                style={{
                    width: 600,
                    boxShadow: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
                    borderRadius: 12,
                    background: 'var(--pt-bg-panel)',
                    border: '1px solid rgba(255,255,255,0.13)',
                }}
            >
                <div style={{textAlign: 'center', marginBottom: 24}}>
                    <Title level={3} style={{color: 'var(--pt-primary)', margin: 0}}>
                        用户测评
                    </Title>
                    <Text style={{color: 'var(--pt-text-secondary)'}}>
                        完成以下测评即可开始使用系统
                    </Text>
                </div>

                <Steps
                    current={currentStep}
                    items={steps}
                    style={{marginBottom: 32}}
                />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        question1Reason: undefined,
                        question2RiskLevel: undefined,
                        question3Amount: undefined,
                    }}
                >
                    {renderQuestionContent()}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 24
                    }}>
                        {currentStep > 0 ? (
                            <Button
                                onClick={handlePrevStep}
                                style={{
                                    background: 'var(--pt-bg-hover)',
                                    borderColor: 'rgba(255,255,255,0.13)',
                                    color: 'var(--pt-text-primary)'
                                }}
                            >
                                上一步
                            </Button>
                        ) : (
                            <div/>
                        )}

                        {currentStep < steps.length - 1 ? (
                            <Button
                                type="primary"
                                onClick={handleNextStep}
                                style={{
                                    background: 'var(--pt-primary)',
                                    borderColor: 'var(--pt-primary)',
                                    color: 'var(--pt-bg-base)',
                                    fontWeight: 600
                                }}
                            >
                                下一步
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitting}
                                style={{
                                    background: 'var(--pt-primary)',
                                    borderColor: 'var(--pt-primary)',
                                    color: 'var(--pt-bg-base)',
                                    fontWeight: 600
                                }}
                            >
                                提交测评
                            </Button>
                        )}
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default AssessmentPage;
